// src/services/blogService.js
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URI;

// Transform blog data helper function
const transformBlogData = (data) => {
  if (!data) {
    console.error('No data provided to transformBlogData');
    return null;
  }

  try {
    const transformed = {
      ...data,
      // Core blog fields
      title: data.title || '',
      urlSlug: data.urlSlug || '',
      content: data.content || '',
      shortDescription: data.shortDescription || '',
      description: data.description || '',
      
      // Categories and tags
      categories: data.categories || [],
      tags: data.tags || [],
      categoryNames: data.categoryNames || [],
      tagNames: data.tagNames || [],
      
      // Author information
      author: data.author || null,
      authorName: data.authorName || (data.author ? `${data.author.firstName} ${data.author.lastName}` : 'CodeITronics'),
      
      // Status and dates
      status: data.status || 'draft',
      publishedAt: data.publishedAt ? new Date(data.publishedAt) : null,
      archivedAt: data.archivedAt ? new Date(data.archivedAt) : null,
      createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
      
      // Blog metadata
      readingTime: data.readingTime || 0,
      featureImage: data.featureImage || '',
      featured: data.featured || false,
      commentsEnabled: data.commentsEnabled ?? true,
      
      // Engagement metrics
      metadata: {
        views: data.metadata?.views || 0,
        likes: data.metadata?.likes || 0,
        shares: data.metadata?.shares || 0
      }
    };

    return transformed;
  } catch (error) {
    console.error('Error transforming blog data:', error);
    return null;
  }
};

class BlogService {
  async getAllBlogs(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 10,
        sortField: params.sortField || 'createdAt',
        sortOrder: params.sortOrder || 'desc',
        ...(params.status && { status: params.status }),
        ...(params.search && { search: params.search }),
        ...(params.featured && { featured: params.featured })
      }).toString();

      const response = await axios.get(
        `${API_BASE_URL}/api/blogs?${queryParams}`,
        { headers: getAuthHeaders() }
      );

      return {
        ...response.data,
        data: response.data.data.map(transformBlogData)
      };
    } catch (error) {
      console.error('Error fetching blogs:', error);
      throw error.response?.data || error;
    }
  }

  async getBlogStats() {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/stats`,
        { headers: getAuthHeaders() }
      );

      const defaultStats = {
        total: 0,
        published: 0,
        draft: 0,
        archived: 0,
        featured: 0
      };

      if (response.data?.data) {
        return {
          totalBlogs: response.data.data.total || 0,
          publishedBlogs: response.data.data.published || 0,
          draftBlogs: response.data.data.draft || 0,
          archivedBlogs: response.data.data.archived || 0,
          featuredBlogs: response.data.data.featured || 0
        };
      }
      return {
        ...defaultStats,
        ...response.data.data
      };
    } catch (error) {
      console.error('Error fetching blog stats:', error);
      return {
        totalBlogs: 0,
        publishedBlogs: 0,
        draftBlogs: 0,
        archivedBlogs: 0,
        featuredBlogs: 0
      };
    }
  }

  async getBlogById(id) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/${id}`,
        { headers: getAuthHeaders() }
      );
      
      if (!response.data || !response.data.data) {
        throw new Error('Invalid response structure from server');
      }

      return transformBlogData(response.data.data);
    } catch (error) {
      console.error('Error in getBlogById:', error);
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to fetch blog');
      }
      throw error;
    }
  }

  async createBlog(data) {
    try {
      // Ensure authorName is included in the data
      if (!data.authorName && data.author) {
        data.authorName = `${data.author.firstName} ${data.author.lastName}`;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/blogs`,
        data,
        { headers: getAuthHeaders() }
      );
      return transformBlogData(response.data.data);
    } catch (error) {
      console.error('Error creating blog:', error);
      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = Object.values(validationErrors).flat();
        throw new Error(errorMessages.join(', '));
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('An unexpected error occurred while creating the blog post');
    }
  }

  async updateBlog(id, data) {
    try {
      // Ensure authorName is included in the data
      if (!data.authorName && data.author) {
        data.authorName = `${data.author.firstName} ${data.author.lastName}`;
      }

      const response = await axios.put(
        `${API_BASE_URL}/api/blogs/${id}`,
        data,
        { headers: getAuthHeaders() }
      );
      return transformBlogData(response.data.data);
    } catch (error) {
      console.error('Error updating blog:', error);
      throw error.response?.data || error;
    }
  }

  async deleteBlog(id) {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/blogs/${id}`,
        { headers: getAuthHeaders() }
      );
      return true;
    } catch (error) {
      console.error('Error deleting blog:', error);
      throw error.response?.data || error;
    }
  }

  async updateBlogStatus(id, status) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/blogs/${id}/status`,
        { status },
        { headers: getAuthHeaders() }
      );
      return transformBlogData(response.data.data);
    } catch (error) {
      console.error('Error updating blog status:', error);
      throw error.response?.data || error;
    }
  }

  async archiveBlog(id) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/blogs/${id}/archive`,
        {},
        { headers: getAuthHeaders() }
      );
      return transformBlogData(response.data.data);
    } catch (error) {
      console.error('Error archiving blog:', error);
      throw error.response?.data || error;
    }
  }

  async getBlogBySlug(urlSlug) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/blogs/slug/${urlSlug}`);
      
      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      // Handle both direct data and wrapped data structures
      const blogData = response.data.data || response.data;
      
      if (!blogData) {
        throw new Error('Blog not found');
      }

      const transformedBlog = transformBlogData(blogData);
      
      if (!transformedBlog) {
        throw new Error('Failed to transform blog data');
      }

      return transformedBlog;
    } catch (error) {
      console.error('Error fetching blog by urlSlug:', error);
      if (error.response?.status === 404) {
        throw new Error('Blog not found');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch blog post');
    }
  }

  async getPublishedBlogs(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 10,
        sortField: params.sortField || 'publishedAt',
        sortOrder: params.sortOrder || 'desc',
        ...(params.featured && { featured: params.featured }),
        ...(params.category && { category: params.category }),
        ...(params.tag && { tag: params.tag }),
        ...(params.search && { search: params.search })
      }).toString();

      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/public?${queryParams}`
      );

      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      // Handle different response structures
      if (response.data.status === 'success' && Array.isArray(response.data.data)) {
        return {
          status: 'success',
          data: response.data.data.map(transformBlogData),
          pagination: response.data.pagination || {
            total: response.data.data.length,
            pages: 1,
            limit: params.limit || 10
          }
        };
      } else if (Array.isArray(response.data)) {
      return {
          status: 'success',
          data: response.data.map(transformBlogData),
          pagination: {
            total: response.data.length,
            pages: 1,
            limit: params.limit || 10
          }
        };
      } else {
        throw new Error('Invalid data structure in response');
      }
    } catch (error) {
      console.error('Error fetching published blogs:', error);
      return {
        status: 'error',
        message: error.response?.data?.message || error.message || 'Failed to fetch published blogs',
        data: [],
        pagination: {
          total: 0,
          pages: 0,
          limit: params.limit || 10
        }
      };
    }
  }

  async incrementViews(id) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/blogs/${id}/views`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error incrementing blog views:', error);
      throw error.response?.data || error;
    }
  }

  async incrementLikes(id) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/blogs/${id}/likes`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error incrementing blog likes:', error);
      throw error.response?.data || error;
    }
  }

  async incrementShares(id) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/blogs/${id}/shares`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error incrementing blog shares:', error);
      throw error.response?.data || error;
    }
  }

  async exportToCSV(blogs) {
    try {
      const headers = ['Title', 'URL Slug', 'Description', 'Status', 'Published At', 'Created At', 'Author'];
      const rows = blogs.map(blog => [
        blog.title,
        blog.urlSlug,
        blog.description,
        blog.status,
        blog.publishedAt ? new Date(blog.publishedAt).toISOString() : '',
        new Date(blog.createdAt).toISOString(),
        blog.authorName
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `blogs_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting blogs to CSV:', error);
      throw error;
    }
  }

  async exportToJSON(blogs) {
    try {
      const jsonContent = JSON.stringify(blogs, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `blogs_${new Date().toISOString()}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting blogs to JSON:', error);
      throw error;
    }
  }

  async importBlogs(blogs, mode = 'merge') {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/blogs/import`,
        { blogs, mode },
        getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error importing blogs:', error);
      throw error.response?.data || error;
    }
  }

  async getRecentPosts(limit = 5) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/public?limit=${limit}&sortField=publishedAt&sortOrder=desc`
      );
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching recent posts:', error);
      return null;
    }
  }

  async getPopularPosts(limit = 5) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/public?limit=${limit}&sortField=metadata.views&sortOrder=desc`
      );
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching popular posts:', error);
      return null;
    }
  }

  async getRelatedPosts(category, currentPostId, limit = 3) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/public?category=${category}&limit=${limit}`
      );
      const posts = response.data.data || [];
      return posts.filter(post => post._id !== currentPostId).slice(0, limit);
    } catch (error) {
      console.error('Error fetching related posts:', error);
      return null;
    }
  }
}

export const blogService = new BlogService();
